import { computed, type CSSProperties, type Ref } from "vue";
import {
  getSliderInputTickPositions,
  normalizeSliderInputConstraints,
  normalizeSliderInputValue,
} from "../utils/sliderInputValue";

interface DSSliderInputValueProps {
  min: number;
  max: number;
  step: number;
  disabled: boolean;
  showTickMarks: boolean;
}

export const useDSSliderInputValue = (props: DSSliderInputValueProps, modelValue: Ref<number>) => {
  const resolvedConstraints = computed(() =>
    normalizeSliderInputConstraints({
      min: props.min,
      max: props.max,
      step: props.step,
    })
  );
  const resolvedMin = computed(() => resolvedConstraints.value.min);
  const resolvedMax = computed(() => resolvedConstraints.value.max);
  const resolvedStep = computed(() => resolvedConstraints.value.step);
  const normalizeValue = (value: number) => normalizeSliderInputValue(value, resolvedConstraints.value);
  const currentValue = computed(() => normalizeValue(modelValue.value));
  const progress = computed(() => {
    const scale = Math.max(Math.abs(resolvedMin.value), Math.abs(resolvedMax.value), Math.abs(currentValue.value), 1);
    const scaledMin = resolvedMin.value / scale;
    const scaledMax = resolvedMax.value / scale;
    const scaledValue = currentValue.value / scale;
    const scaledRange = scaledMax - scaledMin;
    const percentage = scaledRange > 0 ? ((scaledValue - scaledMin) / scaledRange) * 100 : 0;

    return Math.min(Math.max(percentage, 0), 100);
  });
  const sliderStyle = computed<CSSProperties>(() => ({
    "--ds-slider-input-progress": `${progress.value}%`,
  }));
  const tickMarkStyles = computed<CSSProperties[]>(() =>
    props.showTickMarks
      ? getSliderInputTickPositions(resolvedConstraints.value).map(position => ({
          "--ds-slider-input-tick-position": `${position}%`,
        }))
      : []
  );
  const stepperControls = computed(() => ({
    canDecrement: !props.disabled && normalizeValue(currentValue.value - resolvedStep.value) !== currentValue.value,
    canIncrement: !props.disabled && normalizeValue(currentValue.value + resolvedStep.value) !== currentValue.value,
  }));

  return {
    currentValue,
    normalizeValue,
    resolvedMax,
    resolvedMin,
    resolvedStep,
    sliderStyle,
    stepperControls,
    tickMarkStyles,
  };
};
